import { useEffect, useState } from 'react';
import { api } from '../api';
import { useChat } from '../store';
import type { Message } from '../types';
import { relativeTime } from '../utils/time';
import { IconClose, IconStar } from './Icon';

interface PinnedStripProps {
  conversationId: string;
}

type PinnedMessage = Message & { pinnedBy: string; pinnedAt: string };

// Strip rendered between the chat header and the message list. Lists up
// to 3 active pins for the conversation, newest pin first. Clicking a
// pin jumps the message list to that message (highlight + scroll into
// view). Listens for message:pinned / message:unpinned WS events to
// stay live as members pin/unpin from anywhere in the workspace.
export function PinnedStrip({ conversationId }: PinnedStripProps) {
  const [pins, setPins] = useState<PinnedMessage[] | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const setJumpToMessage = useChat((s) => s.setJumpToMessage);
  const setPinnedIds = useChat((s) => s.setPinnedIds);
  const togglePinLocal = useChat((s) => s.togglePinLocal);
  // Used to display the original sender's name on each pin. The
  // conversation participants are the canonical source for display
  // names; falling back to "Someone" when not found (rare — would
  // require the message sender to have left the conversation).
  const conversation = useChat((s) =>
    s.conversations.find((c) => c.id === conversationId),
  );
  const me = useChat((s) => s.me);

  useEffect(() => {
    let cancelled = false;
    setPins(null);
    async function refresh() {
      try {
        const res = await api.listPinnedMessages(conversationId);
        if (cancelled) return;
        setPins(res.pinned);
        // Sync the global pinned-ids set so MessageBubble can render the
        // filled pin button on the active pins.
        setPinnedIds(res.pinned.map((p) => p.id));
      } catch {
        if (!cancelled) setPins([]);
      }
    }
    void refresh();
    // Re-pull on the WS pin/unpin events. Scoped by conversation id so
    // events for other conversations don't trigger our refetch. The local
    // toggle has already updated the store optimistically; the refetch
    // ensures we drop into a consistent server state on rare divergence
    // (e.g. another user simultaneously hit the cap).
    const onPinChange = (e: Event) => {
      const detail = (e as CustomEvent<{
        conversationId: string;
        messageId: string;
        pinned: boolean;
      }>).detail;
      if (detail?.conversationId !== conversationId) return;
      togglePinLocal(detail.messageId, detail.pinned);
      void refresh();
    };
    window.addEventListener('chat:pin-changed', onPinChange);
    return () => {
      cancelled = true;
      window.removeEventListener('chat:pin-changed', onPinChange);
    };
  }, [conversationId, setPinnedIds, togglePinLocal]);

  if (!pins || pins.length === 0) return null;

  return (
    <div className="border-b border-zinc-900 bg-zinc-950/40">
      {collapsed ? (
        // Single-line collapsed view — just count + expand chevron.
        <button
          onClick={() => setCollapsed(false)}
          className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-900/40 transition"
        >
          <IconStar size={11} className="text-amber-400" filled />
          <span className="text-[11px] text-zinc-300">
            {pins.length} pinned message{pins.length === 1 ? '' : 's'}
          </span>
          <span className="ml-auto text-[10px] text-zinc-500">Show</span>
        </button>
      ) : (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-4 pt-2 pb-1">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <IconStar size={10} className="text-amber-400" filled />
              Pinned ({pins.length})
            </span>
            <button
              onClick={() => setCollapsed(true)}
              className="text-zinc-500 hover:text-zinc-200 transition"
              title="Collapse pinned strip"
              aria-label="Collapse pinned strip"
            >
              <IconClose size={12} />
            </button>
          </div>
          <ul className="px-2 pb-2 space-y-0.5">
            {pins.map((pin) => {
              const sender = conversation?.participants.find((p) => p.id === pin.senderId);
              const senderLabel =
                pin.senderId === me?.id
                  ? 'You'
                  : sender?.displayName ?? sender?.email?.split('@')[0] ?? 'Someone';
              const preview =
                pin.body || (pin.attachments?.[0]?.filename ?? 'Pinned message');
              return (
                <li key={pin.id}>
                  <button
                    onClick={() => {
                      // Use the existing jump-to-message store flag — the
                      // MessageList already listens for it and handles
                      // scrolling + flash.
                      setJumpToMessage(pin.id);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-900 transition text-left"
                  >
                    <span className="w-1 self-stretch rounded-full bg-amber-400/70" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                        <span className="font-medium text-zinc-300">{senderLabel}</span>
                        <span aria-hidden>·</span>
                        <span title={new Date(pin.pinnedAt).toLocaleString()}>
                          pinned {relativeTime(pin.pinnedAt)}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-200 truncate">{preview}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
